import { queryOptions } from "@tanstack/react-query";
import {
    createDataSelector,
    createResourceCollectionSelector,
    createResourceSelector,
    handleJsonApiError,
    injectPageParams,
    type PageParams,
    type Relationships,
} from "jsonapi-zod-query";
import { z } from "zod";
import { zj } from "zod-joda";
import { ownerAttributesSchema } from "@/queries/owners.ts";
import { unitAttributesSchema } from "@/queries/units.ts";
import { apiUrl } from "@/utils/api.ts";

export const reservationAttributesSchema = z.object({
    rentalId: z.string(),
    startDate: z.string().transform((strDate) => new Date(strDate)),
    endDate: z.string().transform((strDate) => new Date(strDate)),
    reservationStatus: z.string(),
    customerFirstName: z.string(),
    customerName: z.string(),
    email: z.string().optional(),
    OtherGuests: z.string().optional(),
    confirmationNumber: z.string(),
    carMaker: z.string(),
    carLicense: z.string(),
    dt: zj.zonedDateTime(),
    inputBy: z.enum(["owner", "agent", "Owner", "Agent"]),
    arrived: z.string(),
    ipAddress: z.string(),
});

export const reservationSelector = createDataSelector(
    createResourceSelector({
        type: "reservation",
        attributesSchema: reservationAttributesSchema,
        relationships: {
            calendar: {
                resourceType: "calendar",
                relationshipType: "one",
                include: {
                    type: "calendar",
                    attributesSchema: unitAttributesSchema,
                    relationships: {
                        owner: {
                            resourceType: "owner",
                            relationshipType: "one",
                            include: {
                                type: "owner",
                                attributesSchema: ownerAttributesSchema,
                            },
                        },
                    },
                },
            },
        },
    }),
);

export const reservationCollectionSelector = createResourceCollectionSelector({
    type: "reservation",
    attributesSchema: reservationAttributesSchema,
    relationships: {
        calendar: {
            resourceType: "calendar",
            relationshipType: "one",
            include: {
                type: "calendar",
                attributesSchema: unitAttributesSchema,
                relationships: {
                    owner: {
                        resourceType: "owner",
                        relationshipType: "one",
                        include: {
                            type: "owner",
                            attributesSchema: ownerAttributesSchema,
                        },
                    },
                } satisfies Relationships,
            },
        },
    },
});

export type Reservations = ReturnType<typeof reservationSelector>;
export type PaginatedReservations = ReturnType<typeof reservationCollectionSelector>;
export type ListReservation = PaginatedReservations["data"][number];

export type ListReservationsOptions = {
    pageParams?: PageParams;
};

export const createReservationQueryOptionsFactory = (authFetch: typeof fetch) => ({
    list: (options?: ListReservationsOptions) =>
        queryOptions({
            queryKey: ["reservations", options],
            queryFn: async ({ signal }) => {
                const url = apiUrl("/reservations");
                // url.searchParams.set("fields[reservation]", "title,createdAt");
                injectPageParams(url, options?.pageParams);

                const response = await authFetch(url, { signal });
                await handleJsonApiError(response);
                return reservationCollectionSelector(await response.json());
            },
        }),
    listCurrentAndFuture: () =>
        queryOptions({
            queryKey: ["reservationsListFuture"],
            queryFn: async ({ signal }) => {
                const url = apiUrl("/reservations/currentAndFuture");
                const response = await authFetch(url, { signal });
                await handleJsonApiError(response);
                return reservationCollectionSelector(await response.json());
            },
        }),
    get: (reservationId: string) =>
        queryOptions({
            queryKey: ["reservation", reservationId],
            queryFn: async ({ signal }) => {
                const response = await authFetch(apiUrl(`/reservations/${reservationId}`), {
                    signal,
                });
                await handleJsonApiError(response);
                return reservationSelector(await response.json());
            },
        }),
    getByUnitId: (unitId: string) =>
        queryOptions({
            queryKey: ["reservationUnit", unitId],
            queryFn: async ({ signal }) => {
                const response = await authFetch(apiUrl(`/reservations/unit/${unitId}`), {
                    signal,
                });
                await handleJsonApiError(response);
                return reservationCollectionSelector(await response.json());
            },
        }),
});
